/** A store-only (no compression) zip writer for files that are already
 *  compressed — the Photos tab's JPEG / PNG / WebP originals. Entries are
 *  Blobs and the archive is a Blob composed from them, so the bytes are never
 *  copied into one JS buffer and the browser can keep a large archive
 *  disk-backed (JSZip would hold every file, then the whole archive, in
 *  memory). No ZIP64: an archive must stay under 4 GiB and 65,535 entries —
 *  callers check ZIP_MAX_BYTES up front, and buildZip throws past either. */

/** File bytes a caller should allow in one archive; leaves headroom under the
 *  4 GiB offset limit for the headers. */
export const ZIP_MAX_BYTES = 4_000_000_000;

export interface ZipEntry {
  /** Path inside the archive, "/"-separated (a folder is just a prefix). */
  name: string;
  data: Blob;
  /** CRC-32 of `data` — see crc32(). */
  crc: number;
  /** Modified time as epoch ms — stored as the DOS date/time (local) and as
   *  UTC Unix seconds in an extra field (see unixTimeExtra). */
  modified: number;
}

/* ── CRC-32 ───────────────────────────────────────────────────────────── */

let crcTable: Uint32Array | null = null;

function table(): Uint32Array {
  if (crcTable) return crcTable;
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  crcTable = t;
  return t;
}

const CRC_CHUNK = 4 * 1024 * 1024;

/** CRC-32 of a Blob, read in 4 MB slices so a 30 MB original never needs more
 *  than one slice in memory. */
export async function crc32(blob: Blob): Promise<number> {
  const t = table();
  let c = 0xffffffff;
  for (let at = 0; at < blob.size; at += CRC_CHUNK) {
    const bytes = new Uint8Array(await blob.slice(at, at + CRC_CHUNK).arrayBuffer());
    for (let i = 0; i < bytes.length; i += 1) c = t[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

/* ── Archive ──────────────────────────────────────────────────────────── */

function dosDateTime(ms: number): { date: number; time: number } {
  const d = new Date(ms);
  const year = d.getFullYear();
  // DOS dates run 1980–2107; anything outside (or no date) lands on 1980-01-01.
  if (!Number.isFinite(year) || year < 1980 || year > 2107) return { date: (1 << 5) | 1, time: 0 };
  return {
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
  };
}

const UTF8_NAMES = 0x0800;

/** The old Info-ZIP Unix extra field (0x5855): atime + mtime as UTC Unix
 *  seconds. Archive Utility reads the DOS time with TODAY's DST offset (an
 *  hour out for a March photo unzipped in September) and ignores the newer
 *  0x5455 field, but honours this one — it's what `ditto -c -k` writes. */
function unixTimeExtra(ms: number): ArrayBuffer {
  const secs = Math.min(0xffffffff, Math.max(0, Math.floor(ms / 1000)));
  const x = new DataView(new ArrayBuffer(12));
  x.setUint16(0, 0x5855, true);
  x.setUint16(2, 8, true);
  x.setUint32(4, secs, true); // atime
  x.setUint32(8, secs, true); // mtime
  return x.buffer;
}

/** The zip as one Blob: a local header before each entry's bytes, then the
 *  central directory and its end record. Names are written as UTF-8 (flag
 *  bit 11) so a set label with an accent survives — Archive Utility and
 *  Python honour it; Apple's command-line unzip doesn't. */
export function buildZip(entries: ZipEntry[]): Blob {
  if (entries.length > 0xffff) throw new Error("Too many files for one zip.");
  const enc = new TextEncoder();
  const parts: BlobPart[] = [];
  const central: BlobPart[] = [];
  let offset = 0;
  let centralSize = 0;

  for (const e of entries) {
    const name = enc.encode(e.name);
    const size = e.data.size;
    const { date, time } = dosDateTime(e.modified);
    const extra = unixTimeExtra(e.modified);
    const localSize = 30 + name.length + extra.byteLength;
    if (offset + localSize + size > 0xffffffff) throw new Error("Too large for one zip.");

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true); // version needed
    local.setUint16(6, UTF8_NAMES, true);
    local.setUint16(8, 0, true); // stored
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, e.crc, true);
    local.setUint32(18, size, true); // compressed
    local.setUint32(22, size, true); // uncompressed
    local.setUint16(26, name.length, true);
    local.setUint16(28, extra.byteLength, true);
    parts.push(local.buffer, name, extra, e.data);

    const dir = new DataView(new ArrayBuffer(46));
    dir.setUint32(0, 0x02014b50, true);
    dir.setUint16(4, 20, true); // version made by (MS-DOS attributes)
    dir.setUint16(6, 20, true); // version needed
    dir.setUint16(8, UTF8_NAMES, true);
    dir.setUint16(10, 0, true);
    dir.setUint16(12, time, true);
    dir.setUint16(14, date, true);
    dir.setUint32(16, e.crc, true);
    dir.setUint32(20, size, true);
    dir.setUint32(24, size, true);
    dir.setUint16(28, name.length, true);
    dir.setUint16(30, extra.byteLength, true);
    // 32 comment, 34 disk, 36 internal attrs, 38 external attrs — all 0
    dir.setUint32(42, offset, true);
    central.push(dir.buffer, name, extra);

    offset += localSize + size;
    centralSize += 46 + name.length + extra.byteLength;
  }

  if (offset + centralSize + 22 > 0xffffffff) throw new Error("Too large for one zip.");
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  return new Blob([...parts, ...central, end.buffer], { type: "application/zip" });
}

/** `path`, or `path (2)`, `path (3)`… before the extension — whichever isn't
 *  in `used` yet (compared case-blind, as macOS and Windows unzip). Records
 *  the result in `used`. */
export function uniquePath(path: string, used: Set<string>): string {
  const slash = path.lastIndexOf("/");
  const dot = path.lastIndexOf(".");
  const stem = dot > slash + 1 ? path.slice(0, dot) : path;
  const ext = dot > slash + 1 ? path.slice(dot) : "";
  let out = path;
  for (let n = 2; used.has(out.toLowerCase()); n += 1) out = `${stem} (${n})${ext}`;
  used.add(out.toLowerCase());
  return out;
}
